
import { NetworkDevice, Link, PingResult } from '../types';

export const isValidIP = (ip: string): boolean => {
  const pattern = /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
  return pattern.test(ip);
};

export const ipInSubnet = (ip: string, subnet: string, targetIp: string): boolean => {
  if (!isValidIP(ip) || !isValidIP(targetIp)) return false;
  const ipParts = ip.split('.');
  const targetParts = targetIp.split('.');
  return ipParts[0] === targetParts[0] && 
         ipParts[1] === targetParts[1] && 
         ipParts[2] === targetParts[2];
};

export const findPath = (
  startId: string, 
  endId: string, 
  devices: NetworkDevice[], 
  links: Link[]
): string[] | null => {
  const queue: string[][] = [[startId]];
  const visited = new Set([startId]);

  while (queue.length > 0) {
    const path = queue.shift()!;
    const lastNode = path[path.length - 1];

    if (lastNode === endId) return path;

    const neighbors = links
      .filter(l => l.from === lastNode || l.to === lastNode)
      .map(l => l.from === lastNode ? l.to : l.from);

    for (const neighbor of neighbors) {
      if (!visited.has(neighbor)) {
        visited.add(neighbor);
        queue.push([...path, neighbor]);
      }
    }
  }
  return null;
};

export const simulatePing = (
  sourceId: string,
  targetIp: string,
  devices: NetworkDevice[],
  links: Link[]
): PingResult => {
  const source = devices.find(d => d.id === sourceId);
  const target = devices.find(d => d.ip === targetIp);

  if (!source || source.type !== 'PC') {
    return { success: false, message: "Error: Gunakan PC untuk melakukan tes PING.", path: [] };
  }

  if (!source.ip || !isValidIP(source.ip)) {
    return { success: false, message: "PC Kamu belum diatur IP-nya!", path: [] };
  }

  if (!isValidIP(targetIp)) {
    return { success: false, message: "Alamat IP tujuan salah tulis (Format: x.x.x.x)", path: [] };
  }

  if (!target) {
    return { success: false, message: "Request Timed Out (Alamat IP tidak ditemukan di peta).", path: [] };
  }

  const path = findPath(source.id, target.id, devices, links);
  if (!path) {
    return { success: false, message: "Kabel Putus: Data tidak bisa lewat karena belum ada kabel ke tujuan.", path: [] };
  }

  // KHUSUS: Jika target adalah ISP, kita abaikan cek Firewall karena ISP adalah server publik
  const isTargetISP = target.type === 'ISP';

  const sameSubnet = ipInSubnet(source.ip, "255.255.255.0", target.ip!);

  if (sameSubnet) {
    if (!isTargetISP && !target.firewallDisabled) {
      return { success: false, message: `DIBLOKIR: Data sampai ke ${target.name}, tapi ditolak oleh Firewall. Matikan Firewall di PC tujuan!`, path };
    }
    return { 
      success: true, 
      message: `Reply from ${targetIp}: bytes=32 time<1ms TTL=128 (Koneksi Lokal Berhasil)`, 
      path 
    };
  } else {
    const pathDevices = path.map(id => devices.find(d => d.id === id));
    const hasRouter = pathDevices.some(d => d?.type === 'ROUTER');
    
    if (!hasRouter) {
      return { success: false, message: "Beda Area: IP PC ini dan tujuan beda kelompok, butuh MODEM/ROUTER di tengahnya!", path };
    }

    const gatewayDevice = devices.find(d => d.type === 'ROUTER' && d.ip === source.gateway);
    if (!source.gateway || !gatewayDevice) {
      return { success: false, message: "Gateway Salah: PC ini tidak tahu jalan keluar. Isi Gateway dengan IP Modem!", path };
    }

    // Cek Firewall jika tujuan bukan ISP
    if (!isTargetISP && !target.firewallDisabled) {
      return { success: false, message: `DIBLOKIR: Data sudah lewat Modem, tapi PC tujuan (${target.name}) masih menutup Firewall-nya.`, path };
    }

    return { success: true, message: `Reply from ${targetIp}: Berhasil Online via Gateway ${source.gateway}`, path };
  }
};
